import React, { useEffect, useState } from "react";
import "./horizonTable.css";
import { toast } from "react-toastify";
import {
  adminEditModalOverlayStyle,
  adminEditModalHeaderStyle,
  adminEditModalTitleStyle,
  adminEditModalBodyStyle,
  adminEditModalFooterStyle,
  adminEditCancelBtnStyle,
  adminEditSaveBtnStyle,
  adminEditLabelStyle,
  adminEditInputStyle,
  ADMIN_THEME,
} from "./adminEditModalTheme";

const THEME = "#8b1634";

const directorModalContentStyle = {
  border: "none",
  borderRadius: 16,
  overflow: "hidden",
  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.18)",
};

const directorFieldStyle = {
  ...adminEditInputStyle,
  backgroundColor: "#fafafa",
  color: "#1f2937",
  boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)",
};

const directorInputIconStyle = {
  backgroundColor: "#fdf2f4",
  border: "1px solid #e2e8f0",
  borderRight: "none",
  color: ADMIN_THEME,
  minWidth: 42,
  justifyContent: "center",
};

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-GB");
}

const emptyDirector = { name: "", email: "", phoneNumber: "" };

export default function DirectorsPage() {
  const [directors, setDirectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newDirector, setNewDirector] = useState(emptyDirector);
  const [editDirector, setEditDirector] = useState(null);

  useEffect(() => {
    fetchDirectors();
  }, []);

  async function fetchDirectors() {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${getBaseUrl()}/api/directors`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success && Array.isArray(data.data)) {
        setDirectors(data.data);
      } else {
        setDirectors([]);
        toast.error(data.message || "Failed to load directors");
      }
    } catch {
      setDirectors([]);
      toast.error("Failed to load directors");
    } finally {
      setLoading(false);
    }
  }

  const handleAdd = async () => {
    if (!newDirector.name?.trim() || !newDirector.email?.trim()) {
      toast.error("Name and email are required");
      return;
    }
    if (!isValidEmail(newDirector.email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${getBaseUrl()}/api/directors`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(newDirector),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchDirectors();
        setShowAddModal(false);
        setNewDirector(emptyDirector);
        toast.success("Director added");
      } else {
        toast.error(data.message || "Failed to add director");
      }
    } catch {
      toast.error("Failed to add director");
    }
  };

  const handleEditSave = async () => {
    if (!editDirector?.name?.trim() || !editDirector?.email?.trim()) {
      toast.error("Name and email are required");
      return;
    }
    if (!isValidEmail(editDirector.email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${getBaseUrl()}/api/directors/${editDirector._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: editDirector.name,
          email: editDirector.email,
          phoneNumber: editDirector.phoneNumber,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchDirectors();
        setShowEditModal(false);
        setEditDirector(null);
        toast.success("Director updated");
      } else {
        toast.error(data.message || "Failed to update director");
      }
    } catch {
      toast.error("Failed to update director");
    }
  };

  const filtered = directors.filter((d) => {
    const fields = [d.name, d.email, d.phoneNumber].map((v) => (v || "").toLowerCase());
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length === 0 || words.every((w) => fields.some((f) => f.includes(w)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const renderFormFields = (values, onChange) => (
    <div className="row g-3">
      <div className="col-12">
        <label style={adminEditLabelStyle}>Name</label>
        <div className="input-group">
          <span className="input-group-text" style={directorInputIconStyle}><i className="fa fa-user"></i></span>
          <input
            type="text"
            className="form-control"
            style={directorFieldStyle}
            value={values.name}
            onChange={(e) => onChange({ ...values, name: e.target.value })}
            placeholder="Director name"
          />
        </div>
      </div>
      <div className="col-12">
        <label style={adminEditLabelStyle}>Email</label>
        <div className="input-group">
          <span className="input-group-text" style={directorInputIconStyle}><i className="fa fa-envelope"></i></span>
          <input
            type="email"
            className="form-control"
            style={directorFieldStyle}
            value={values.email}
            onChange={(e) => onChange({ ...values, email: e.target.value })}
            placeholder="Email address"
          />
        </div>
      </div>
      <div className="col-12">
        <label style={adminEditLabelStyle}>Phone number</label>
        <div className="input-group">
          <span className="input-group-text" style={directorInputIconStyle}><i className="fa fa-phone"></i></span>
          <input
            type="text"
            className="form-control"
            style={directorFieldStyle}
            value={values.phoneNumber}
            onChange={(e) => onChange({ ...values, phoneNumber: e.target.value.replace(/\D/g, "").slice(0, 12) })}
            placeholder="Up to 12 digits"
            maxLength={12}
            inputMode="numeric"
            pattern="[0-9]*"
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div
        style={{
          background: "#fff",
          borderRadius: 20,
          boxShadow: "0px 4px 20px rgba(0,0,0,0.08)",
          padding: 24,
          marginBottom: 24,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>Director</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <i className="fa fa-search" style={{ position: "absolute", left: 12, color: THEME, fontSize: 14 }} />
              <input
                type="text"
                placeholder="Type to search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{
                  padding: "10px 12px 10px 36px",
                  borderRadius: 12,
                  border: "1px solid #e2e8f0",
                  fontSize: 14,
                  outline: "none",
                  width: 250,
                }}
              />
            </div>
            {directors.length === 0 && (
              <button
                type="button"
                onClick={() => setShowAddModal(true)}
                style={{
                  background: THEME,
                  color: "#fff",
                  fontWeight: 600,
                  padding: "10px 20px",
                  borderRadius: 12,
                  border: "none",
                  cursor: "pointer",
                  fontSize: 14,
                }}
              >
                <i className="fa fa-plus" style={{ marginRight: 8 }} />
                Add Director
              </button>
            )}
          </div>
        </div>

        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.2fr 1.5fr 1fr 1fr 1fr 120px",
              background: "#f7fafc",
              padding: "12px 16px",
              borderBottom: "1px solid #e2e8f0",
              gap: 16,
              minWidth: 720,
            }}
          >
            <div style={{ fontWeight: 600, color: THEME, fontSize: 14 }}>Name</div>
            <div style={{ fontWeight: 600, color: THEME, fontSize: 14 }}>Email</div>
            <div style={{ fontWeight: 600, color: THEME, fontSize: 14 }}>Phone number</div>
            <div style={{ fontWeight: 600, color: THEME, fontSize: 14 }}>Created</div>
            <div style={{ fontWeight: 600, color: THEME, fontSize: 14 }}>Updated</div>
            <div style={{ fontWeight: 600, color: THEME, fontSize: 14 }}>Actions</div>
          </div>
          {loading ? (
            <div style={{ textAlign: "center", padding: 48, color: THEME, fontWeight: 600 }}>
              <i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />
              Loading...
            </div>
          ) : paged.length === 0 ? (
            <div style={{ textAlign: "center", padding: 48, color: "#718096" }}>No directors found.</div>
          ) : (
            paged.map((d) => (
              <div
                key={d._id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 1.5fr 1fr 1fr 1fr 120px",
                  padding: "14px 16px",
                  borderBottom: "1px solid #f1f5f9",
                  gap: 16,
                  alignItems: "center",
                  minWidth: 720,
                }}
              >
                <div style={{ color: "#2d3748", fontSize: 14 }}>{d.name || "—"}</div>
                <div style={{ color: "#2d3748", fontSize: 14, wordBreak: "break-word" }}>{d.email || "—"}</div>
                <div style={{ color: "#2d3748", fontSize: 14 }}>{d.phoneNumber || "—"}</div>
                <div style={{ color: "#64748b", fontSize: 13 }}>{formatDateTime(d.createdAt)}</div>
                <div style={{ color: "#64748b", fontSize: 13 }}>{formatDateTime(d.updatedAt)}</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    title="Edit"
                    onClick={() => {
                      setEditDirector({ ...d });
                      setShowEditModal(true);
                    }}
                    style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}
                  >
                    <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button
            type="button"
            className="horizon-table-btn"
            disabled={page === 1 || filtered.length === 0}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </button>
          <span style={{ fontSize: 14, color: "#64748b" }}>
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            className="horizon-table-btn"
            disabled={page >= totalPages || filtered.length === 0}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      </div>

      {showAddModal && (
        <div className="modal fade show" style={adminEditModalOverlayStyle} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={directorModalContentStyle}>
              <div className="modal-header" style={adminEditModalHeaderStyle}>
                <h5 className="modal-title" style={adminEditModalTitleStyle}>
                  <i className="fa fa-user-plus" style={{ marginRight: 8, fontSize: 16, opacity: 0.9 }}></i>
                  Add Director
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowAddModal(false)} />
              </div>
              <div className="modal-body" style={adminEditModalBodyStyle}>
                {renderFormFields(newDirector, setNewDirector)}
              </div>
              <div className="modal-footer d-flex justify-content-between align-items-center" style={adminEditModalFooterStyle}>
                <button type="button" className="btn" style={adminEditCancelBtnStyle} onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn" style={adminEditSaveBtnStyle} onClick={handleAdd}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editDirector && (
        <div className="modal fade show" style={adminEditModalOverlayStyle} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={directorModalContentStyle}>
              <div className="modal-header" style={adminEditModalHeaderStyle}>
                <h5 className="modal-title" style={adminEditModalTitleStyle}>
                  <i className="fa fa-edit" style={{ marginRight: 8, fontSize: 16, opacity: 0.9 }}></i>
                  Edit Director
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditDirector(null);
                  }}
                />
              </div>
              <div className="modal-body" style={adminEditModalBodyStyle}>
                {renderFormFields(editDirector, setEditDirector)}
              </div>
              <div className="modal-footer d-flex justify-content-between align-items-center" style={adminEditModalFooterStyle}>
                <button
                  type="button"
                  className="btn"
                  style={adminEditCancelBtnStyle}
                  onClick={() => {
                    setShowEditModal(false);
                    setEditDirector(null);
                  }}
                >
                  Cancel
                </button>
                <button type="button" className="btn" style={adminEditSaveBtnStyle} onClick={handleEditSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
